// src/components/pricing/CheckoutButton.tsx
import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { useTeacherAuth } from '@/contexts/TeacherAuthContext'
import { openAccessPassCheckout, openOneTimeCheckout } from '@/services/payment/razorpayService'
import { openDodoAccessCheckout, openDodoEmergencyCheckout } from '@/services/payment/dodoService'

interface CheckoutButtonProps {
  type: 'access-pass' | 'one-time'
  planId?: string
  tier?: 'monthly' | 'annual'
  amount?: number
  description?: string
  children: React.ReactNode
  disabled?: boolean
  className?: string
  paymentGateway: 'razorpay' | 'dodo' // NEW: Gateway selection
}

export const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  type,
  planId,
  tier,
  amount,
  description,
  children,
  disabled = false,
  className = '',
  paymentGateway,
}) => {
  const { user, profile } = useTeacherAuth()
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    if (!user || !profile) {
      alert('Please sign in to continue')
      return
    }
  
    setLoading(true)
  
    try {
      if (type === 'access-pass') {
        if (!tier) {
          throw new Error('Invalid access pass configuration')
        }
      
        // Route to correct payment gateway
        if (paymentGateway === 'razorpay') {
          await openAccessPassCheckout({
            teacherId: user.uid,
            email: profile.email,
            name: profile.displayName,
            tier,
          })
        } else {
          await openDodoAccessCheckout({
            teacherId: user.uid,
            email: profile.email,
            name: profile.displayName,
            tier,
          })
        }
      } else if (type === 'one-time') {
        if (!amount || !description) {
          throw new Error('Invalid payment configuration')
        }
      
        // Route to correct payment gateway
        if (paymentGateway === 'razorpay') {
          await openOneTimeCheckout({
            amount,
            teacherId: user.uid,
            email: profile.email,
            name: profile.displayName,
            description,
          })
        } else {
          await openDodoEmergencyCheckout({
            teacherId: user.uid,
            email: profile.email,
            name: profile.displayName,
          })
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error)
      
      // Show user-friendly error messages
      let errorMessage = 'Payment failed. Please try again.'
        
      if (error.message?.includes('already have an active')) {
          errorMessage = 'You already have an active subscription.'
      } else if (error.message?.includes('Failed to load')) {
          errorMessage = 'Could not load payment gateway. Please check your internet connection.'
      } else if (error.code === 'permission-denied') {
          errorMessage = 'Permission denied. Please sign in again.'
      } else if (error.message?.includes('payment in progress')) {
          errorMessage = 'You have a payment in progress. Please complete it first.'
      } else if (error.message?.includes('not found')) {
          errorMessage = 'Account not found. Please sign in again.'
      } else if (error.code === 'unavailable') {
          errorMessage = 'Service temporarily unavailable. Please try again in a moment.'
      }
      
      alert(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || loading}
      className={className}
    >
      {loading ? 'Processing...' : children}
    </Button>
  )
}